import { HttpResponse } from '@angular/common/http';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { AuthenticationService } from 'src/app/api/services/authentication.service';
import * as fromActions from '../actions/user.action';
import * as staticDataActions from '../actions/static-data.action';
import { catchError, first, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { PersonDto } from 'src/app/api/models';
import { SessionStorageService } from 'src/app/shared/services/session-storage.service';
import { USER_KEY } from 'src/app/shared/helpers/constants';
import { PushMessageService } from 'src/app/shared/services/push-message.service';
import { UserProfileService } from 'src/app/api/services';
import { ToastrMsgService } from 'src/app/shared/services/toastr-msg.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { CustomTranslationService } from 'src/app/shared/translation/custom-translation.service';
import { CsrfInterceptorInterceptor } from 'src/app/shared/interceptors/csrf-interceptor.interceptor';

@Injectable()
export class UserEffect {
  constructor(
    private actions$: Actions,
    private auth: AuthenticationService,
    private userProfileService: UserProfileService,
    private router: Router,
    private route: ActivatedRoute,
    private session: SessionStorageService,
    private pushMessageService: PushMessageService,
    private toastrMsg: ToastrMsgService,
    private localStorageService: LocalStorageService,
    private translationService: CustomTranslationService
  ) { }

  // ------------   LOGIN  -------------------------------------------------
  loginUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loginUser),
      switchMap(({ rememberme, body }) => {
        return this.auth.login$Response({ rememberme, body }).pipe(
          map((resp: HttpResponse<PersonDto>) => resp.body as PersonDto),
          map(user => {
            this.session?.replace(USER_KEY, JSON.stringify(user));
            return fromActions.loginUserSuccess({ user });
          }),
          catchError(error => {
            return of(fromActions.loginUserError(error));
          })
        );
      })
    )
  );

  loginSuccessRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.loginUserSuccess),
        tap(() => {
          const language = this.localStorageService.getLanguage();
          if (language) {
            this.translationService.isLangReady$.next(false);
            this.translationService.changeLoaderLoadLang(language);
          }
          this.route.queryParams.pipe(first()).subscribe((p: Params) => {
            if (p.returnUrl) {
              this.router.navigate([p.returnUrl]);
            } else {
              this.router.navigate(['/calendar']);
            }
          });
        })
      ),
    { dispatch: false }
  );

  // ------------   LOGOUT  -------------------------------------------------
  logoutUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.logoutUser),
      switchMap(() => {
        // disconnect WS must be done before the actual logout, else it fails due to missing authentication
        this.pushMessageService.disconnect();
        return this.auth.logout().pipe(
          map(() => {
            CsrfInterceptorInterceptor.csrfToken = '';
            this.session?.remove(USER_KEY);
            this.session?.remove('currentState');
            return fromActions.logoutUserSuccess();
          })
        );
      })
    )
  );

  logoutSuccessRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.logoutUserSuccess),
        tap(() => {
          this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );

  switchTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.switchTenant),
      switchMap(({ tenantDiscr }) => {
        return this.userProfileService.switchTenant$Response({ body: { tenantDiscr } }).pipe(
          map((resp: HttpResponse<PersonDto>) => resp.body as PersonDto),
          map(user => {
            this.toastrMsg.showSuccess('tenant.toaster.switch.success');
            this.session?.replace(USER_KEY, JSON.stringify(user));
            return [fromActions.switchTenantSuccess({ user }), staticDataActions.clearSettingsData()];
          }),
          mergeMap(actions => actions),
          catchError(error => {
            console.error('switch tenant failed', error);
            this.toastrMsg.showError('tenant.toaster.switch.failiure');
            return of(fromActions.switchTenantError(error));
          })
        );
      })
    )
  );

  switchTenantBack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.switchTenantBack),
      switchMap(({ tenantDiscr }) => {
        return this.userProfileService.switchTenant$Response({ body: { tenantDiscr } }).pipe(
          map((resp: HttpResponse<PersonDto>) => resp.body as PersonDto),
          map(user => {
            this.toastrMsg.showSuccess('tenant.toaster.switch.success');
            this.session?.replace(USER_KEY, JSON.stringify(user));
             return [fromActions.switchTenantBackSuccess({ user }), staticDataActions.clearSettingsData()];
          }),
          mergeMap(actions => actions),
          catchError(error => {
            console.error('switch tenant back failed', error);
            this.toastrMsg.showError('tenant.toaster.switch.failiure');
            return of(fromActions.switchTenantError(error));
          })
        );
      })
    )
  );

  changeOrgUnit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.changeOrgUnit),
      switchMap(({ organizationalUnitId }) => {
        return this.userProfileService.changeOrgUnit$Response({ body: { organizationalUnitId } }).pipe(
          map((resp: HttpResponse<PersonDto>) => resp.body as PersonDto),
          map(user => {
            this.toastrMsg.showSuccess('tenant.toaster.change.orgUnit.success');
            this.session?.replace(USER_KEY, JSON.stringify(user));
            window.location.reload();
            return fromActions.changeOrgUnitSuccess({ user });
          }),
          catchError(error => {
            console.error('change org unit failed', error);
            this.toastrMsg.showError('tenant.toaster.change.orgUnit.failiure');
            return of(fromActions.changeOrgUnitError(error));
          })
        );
      })
    )
  );
}
